import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { db, messaging } from "../lib/firebase";
import { getToken } from "firebase/messaging";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";

const VAPID_KEY =
  "BE5t2IRd1DiN6LP1YAgs6Y8Z5a5wWf-eCKI4RfLNdLHjdl-FkXdq70fO7KTl-NPMKELiZ67WO7uNxaMVN-BYX5E";

// ─── Animations ──────────────────────────────────────────────────────────────

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

// ─── Styled components ────────────────────────────────────────────────────────

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  align-items: flex-end;
`;

const Sheet = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.large} ${({ theme }) => theme.radii.large} 0 0;
  padding: ${({ theme }) => theme.spacing.xlarge} ${({ theme }) => theme.spacing.large}
    calc(${({ theme }) => theme.spacing.xlarge} + env(safe-area-inset-bottom));
  box-shadow: ${({ theme }) => theme.shadows.strong};
  animation: ${slideUp} ${({ theme }) => theme.transitions.normal} both;
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.subheading};
  font-weight: ${({ theme }) => theme.typography.fontWeight.heading};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.small};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`;

const Body = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.body};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0 0 ${({ theme }) => theme.spacing.large};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

const ButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`;

const PrimaryBtn = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radii.pill};
  font-family: ${({ theme }) => theme.typography.fontFamily.body};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    opacity: 0.88;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GhostBtn = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.medium};
  background: transparent;
  color: ${({ theme }) => theme.colors.textLight};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.pill};
  font-family: ${({ theme }) => theme.typography.fontFamily.body};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background};
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const NotificationPrompt = () => {
  // ↓ Absolute first line — fires before any hooks, every render
  console.log("NotificationPrompt mounted");

  const { currentUser } = useAuth();
  // null = still loading, true = show, false = hide
  const [visible, setVisible] = useState(null);
  const [loading, setLoading] = useState(false);

  console.log("[NotificationPrompt] rendered — currentUser:", currentUser?.uid ?? "none", "visible:", visible);

  // Check Firestore on mount to decide whether to show the prompt
  useEffect(() => {
    if (!currentUser) {
      console.log("[NotificationPrompt] no currentUser yet, staying hidden");
      setVisible(false);
      return;
    }

    // Log full Notification API diagnostics so we can see exactly what mobile reports
    const notificationInWindow = "Notification" in window;
    const notificationDefined = typeof Notification !== "undefined";
    const existingPermission = notificationInWindow ? Notification.permission : "N/A";
    console.log("[NotificationPrompt] Notification API diagnostics:", {
      notificationInWindow,
      notificationDefined,
      existingPermission,
      userAgent: navigator.userAgent,
      standalone: window.matchMedia?.("(display-mode: standalone)").matches ?? false,
    });

    // Don't bother if the browser doesn't support the Notifications API
    if (!notificationInWindow) {
      console.log("[NotificationPrompt] Notifications API not in window — hiding");
      setVisible(false);
      return;
    }

    const checkPermission = async () => {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          // Doc doesn't exist yet — no notificationPermission field, so show prompt
          console.log("[NotificationPrompt] user doc does not exist — showing prompt");
          setVisible(true);
          return;
        }

        const data = userSnap.data();
        console.log("[NotificationPrompt] user doc data:", data);

        // Show the prompt only when the field is completely absent
        const hasPermissionField =
          Object.prototype.hasOwnProperty.call(data, "notificationPermission");

        console.log("[NotificationPrompt] hasPermissionField:", hasPermissionField, "→ visible:", !hasPermissionField);
        setVisible(!hasPermissionField);
      } catch (err) {
        console.warn("[NotificationPrompt] failed to read user doc:", err);
        setVisible(false);
      }
    };

    checkPermission();
  }, [currentUser]);

  // Token refresh — runs on mount when permission is already granted.
  // The Firebase JS SDK v9 modular API has no onTokenRefresh() event; the
  // correct approach is to call getToken() and compare with what's stored.
  useEffect(() => {
    if (!currentUser) return;

    const checkTokenRefresh = async () => {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) return;

        const { notificationPermission, fcmToken: storedToken } =
          userSnap.data();

        // Only run for users who have already granted permission
        if (notificationPermission !== "granted") return;

        const messagingInstance = await messaging;
        if (!messagingInstance) return;

        const latestToken = await getToken(messagingInstance, {
          vapidKey: VAPID_KEY,
        });

        if (latestToken && latestToken !== storedToken) {
          await updateDoc(userRef, { fcmToken: latestToken });
          console.log("[NotificationPrompt] FCM token refreshed in Firestore");
        }
      } catch (err) {
        // Token refresh is best-effort — log but don't surface to the user
        console.warn("[NotificationPrompt] token refresh check failed:", err);
      }
    };

    checkTokenRefresh();
  }, [currentUser]);

  const handleTurnOn = async () => {
    if (!currentUser) return;
    setLoading(true);

    try {
      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        // messaging is a Promise — must be awaited
        const messagingInstance = await messaging;

        if (messagingInstance) {
          try {
            const token = await getToken(messagingInstance, {
              vapidKey: VAPID_KEY,
            });

            const userRef = doc(db, "users", currentUser.uid);
            await updateDoc(userRef, {
              fcmToken: token,
              notificationPermission: "granted",
            });
          } catch (tokenErr) {
            console.warn("NotificationPrompt: could not get FCM token", tokenErr);
            // Still record that they granted permission even if token fetch failed
            const userRef = doc(db, "users", currentUser.uid);
            await updateDoc(userRef, { notificationPermission: "granted" });
          }
        } else {
          // Messaging not supported (e.g. iOS Safari) — just record granted
          const userRef = doc(db, "users", currentUser.uid);
          await updateDoc(userRef, { notificationPermission: "granted" });
        }
      } else {
        // Browser denied (user dismissed or blocked)
        const userRef = doc(db, "users", currentUser.uid);
        await updateDoc(userRef, { notificationPermission: "denied" });
      }
    } catch (err) {
      console.error("NotificationPrompt: error requesting permission", err);
    } finally {
      setVisible(false);
      setLoading(false);
    }
  };

  const handleMaybeLater = () => {
    // Session-only dismiss — no Firestore write
    setVisible(false);
  };

  // Don't render until we've confirmed the prompt should be shown
  if (!visible) return null;

  return (
    <Backdrop>
      <Sheet>
        <Title>Stay in the loop</Title>
        <Body>
          Get deadline reminders before your picks lock, and updates when scores are in.
        </Body>
        <ButtonStack>
          <PrimaryBtn onClick={handleTurnOn} disabled={loading}>
            {loading ? "Setting up…" : "Turn on notifications"}
          </PrimaryBtn>
          <GhostBtn onClick={handleMaybeLater} disabled={loading}>
            Maybe later
          </GhostBtn>
        </ButtonStack>
      </Sheet>
    </Backdrop>
  );
};

export default NotificationPrompt;
